import * as Sentry from "@sentry/react";
import coralogixLogger from "./coralogix";

interface LogContext {
  [key: string]: unknown;
}

interface ErrorLogContext extends LogContext {
  component?: string;
  action?: string;
  userId?: string;
  sessionId?: string;
  url?: string;
  userAgent?: string;
  timestamp?: string;
}

class Logger {
  private static sessionId: string | null = null;

  private static getSessionId(): string {
    if (!Logger.sessionId) {
      Logger.sessionId =
        sessionStorage.getItem("sessionId") ||
        localStorage.getItem("sessionId") ||
        `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

      if (!sessionStorage.getItem("sessionId")) {
        sessionStorage.setItem("sessionId", Logger.sessionId);
      }
    }
    return Logger.sessionId;
  }

  private static enrichContext(context: LogContext = {}): LogContext {
    return {
      ...context,
      sessionId: Logger.getSessionId(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };
  }

  static info(message: string, context?: LogContext): void {
    const enrichedContext = Logger.enrichContext(context);

    console.info(message, enrichedContext);
    coralogixLogger.log(message, enrichedContext, "info");
  }

  static warn(message: string, context?: LogContext): void {
    const enrichedContext = Logger.enrichContext(context);

    console.warn(message, enrichedContext);
    coralogixLogger.log(message, enrichedContext, "warn");
  }

  static error(
    message: string,
    error?: Error | unknown,
    context?: ErrorLogContext
  ): void {
    const enrichedContext = Logger.enrichContext(context);

    console.error(message, error, enrichedContext);

    // Log to Coralogix
    coralogixLogger.logError(message, error, enrichedContext);

    // Log to Sentry with context
    Sentry.withScope((scope) => {
      // Set tags for filtering
      scope.setTag("logger.level", "error");
      scope.setTag("logger.source", "marketing-frontend");

      if (enrichedContext.component) {
        scope.setTag("component", enrichedContext.component as string);
      }

      if (enrichedContext.action) {
        scope.setTag("action", enrichedContext.action as string);
      }

      // Set context data
      scope.setContext("logContext", enrichedContext);
      scope.setContext("errorDetails", {
        message,
        sessionId: enrichedContext.sessionId,
        timestamp: enrichedContext.timestamp,
      });

      // Set level
      scope.setLevel("error");

      // Capture exception or message
      if (error) {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(message, "error");
      }
    });
  }

  static debug(message: string, context?: LogContext): void {
    const enrichedContext = Logger.enrichContext(context);

    console.debug(message, enrichedContext);
    coralogixLogger.log(message, enrichedContext, "debug");
  }

  static setUserContext(user: {
    id?: string;
    email?: string;
    [key: string]: unknown;
  }): void {
    // Set user context for both logging systems
    coralogixLogger.addUserContext(user);

    Sentry.setUser({
      id: user.id,
      email: user.email,
      ...user,
    });
  }

  static addTag(key: string, value: string): void {
    coralogixLogger.addCustomAttribute(key, value);
    Sentry.setTag(key, value);
  }

  static captureException(error: Error, context?: ErrorLogContext): void {
    Logger.error("Exception captured", error, context);
  }

  static captureMessage(
    message: string,
    level: "info" | "warning" | "error" = "info",
    context?: LogContext
  ): void {
    const enrichedContext = Logger.enrichContext(context);

    switch (level) {
      case "info":
        Logger.info(message, enrichedContext);
        break;
      case "warning":
        Logger.warn(message, enrichedContext);
        break;
      case "error":
        Logger.error(message, undefined, enrichedContext);
        break;
    }
  }
}

export default Logger;
